export const createTutorialAmbient = () => {
  if (typeof window === 'undefined') return null;

  const TutorialAudioContext = window.AudioContext || window.webkitAudioContext;
  if (!TutorialAudioContext) return null;

  const audioContext = new TutorialAudioContext();
  const now = audioContext.currentTime;
  const masterGain = audioContext.createGain();
  const lowpass = audioContext.createBiquadFilter();
  const highpass = audioContext.createBiquadFilter();
  const shimmerFilter = audioContext.createBiquadFilter();
  const padOscA = audioContext.createOscillator();
  const padOscB = audioContext.createOscillator();
  const shimmerOsc = audioContext.createOscillator();
  const padGainA = audioContext.createGain();
  const padGainB = audioContext.createGain();
  const shimmerGain = audioContext.createGain();
  const pulseOsc = audioContext.createOscillator();
  const pulseGain = audioContext.createGain();
  const motionOsc = audioContext.createOscillator();
  const motionGain = audioContext.createGain();
  const guitarChord = [164.81, 207.65, 246.94];
  const guitarOscillators = [];
  const guitarGains = [];
  let guitarIntervalId = null;

  masterGain.gain.setValueAtTime(0.0001, now);
  lowpass.type = 'lowpass';
  lowpass.frequency.setValueAtTime(880, now);
  lowpass.Q.setValueAtTime(0.24, now);
  highpass.type = 'highpass';
  highpass.frequency.setValueAtTime(90, now);
  shimmerFilter.type = 'highshelf';
  shimmerFilter.frequency.setValueAtTime(2400, now);
  shimmerFilter.gain.setValueAtTime(2.4, now);

  padOscA.type = 'triangle';
  padOscA.frequency.setValueAtTime(164.81, now);
  padGainA.gain.setValueAtTime(0.056, now);

  padOscB.type = 'triangle';
  padOscB.frequency.setValueAtTime(207.65, now);
  padGainB.gain.setValueAtTime(0.042, now);

  shimmerOsc.type = 'sine';
  shimmerOsc.frequency.setValueAtTime(329.63, now);
  shimmerGain.gain.setValueAtTime(0.015, now);

  pulseOsc.type = 'sine';
  pulseOsc.frequency.setValueAtTime(0.045, now);
  pulseGain.gain.setValueAtTime(0.04, now);

  motionOsc.type = 'sine';
  motionOsc.frequency.setValueAtTime(0.09, now);
  motionGain.gain.setValueAtTime(5.5, now);

  pulseOsc.connect(pulseGain.gain);
  motionOsc.connect(motionGain.gain);
  motionGain.connect(padOscA.detune);
  motionGain.connect(padOscB.detune);
  padOscA.connect(padGainA);
  padOscB.connect(padGainB);
  shimmerOsc.connect(shimmerGain);
  padGainA.connect(masterGain);
  padGainB.connect(masterGain);
  shimmerGain.connect(masterGain);
  pulseGain.connect(masterGain);
  masterGain.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(shimmerFilter);
  shimmerFilter.connect(audioContext.destination);

  guitarChord.forEach((frequency, index) => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.type = index === 1 ? 'triangle' : 'sine';
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.detune.setValueAtTime(index === 2 ? 3 : -2, now);
    gainNode.gain.setValueAtTime(0.0001, now);
    oscillator.connect(gainNode);
    gainNode.connect(masterGain);
    oscillator.start(now);
    guitarOscillators.push(oscillator);
    guitarGains.push(gainNode);
  });

  padOscA.start(now);
  padOscB.start(now);
  shimmerOsc.start(now);
  pulseOsc.start(now);
  motionOsc.start(now);

  masterGain.gain.exponentialRampToValueAtTime(0.235, now + 1.8);
  lowpass.frequency.linearRampToValueAtTime(1120, now + 2.8);
  shimmerOsc.frequency.linearRampToValueAtTime(392, now + 4.2);
  shimmerGain.gain.linearRampToValueAtTime(0.022, now + 3.8);

  const strumGuitar = () => {
    const baseTime = audioContext.currentTime + 0.03;
    guitarGains.forEach((gainNode, index) => {
      const hitTime = baseTime + index * 0.048;
      gainNode.gain.cancelScheduledValues(hitTime);
      gainNode.gain.setValueAtTime(0.0001, hitTime);
      gainNode.gain.linearRampToValueAtTime(index === 0 ? 0.026 : 0.019, hitTime + 0.045);
      gainNode.gain.exponentialRampToValueAtTime(0.00012, hitTime + 1.4);
    });
  };

  strumGuitar();
  guitarIntervalId = window.setInterval(strumGuitar, 2550);

  return {
    resume: () => audioContext.resume().catch(() => undefined),
    stop: () => {
      if (guitarIntervalId) {
        window.clearInterval(guitarIntervalId);
      }
      const stopTime = audioContext.currentTime + 0.9;
      masterGain.gain.cancelScheduledValues(audioContext.currentTime);
      masterGain.gain.setValueAtTime(Math.max(masterGain.gain.value, 0.0001), audioContext.currentTime);
      masterGain.gain.exponentialRampToValueAtTime(0.0001, stopTime);
      [...guitarOscillators, padOscA, padOscB, shimmerOsc, pulseOsc, motionOsc].forEach((oscillator) => {
        try {
          oscillator.stop(stopTime);
        } catch (error) {
          // no-op
        }
      });
      window.setTimeout(() => {
        audioContext.close().catch(() => undefined);
      }, 1100);
    },
  };
};

export const createAmbientTrack = (src, volume = 0.22) => {
  if (typeof window === 'undefined' || !src) return null;

  const audio = new Audio(src);
  audio.loop = true;
  audio.volume = volume;

  return {
    resume: () => audio.play().catch(() => undefined),
    stop: () => {
      audio.pause();
      audio.currentTime = 0;
    },
  };
};
